import { useEffect, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import * as CompanyForm from 'assets/js/pages/contacts/companies/CompanyForm';
import { ContactFormParams } from 'assets/js/pages/contacts/contactFormParams';
import { useDefaultContactFormValues } from 'assets/js/pages/contacts/hooks/useDefaultContactFormValues';
import { useSaveContact } from 'assets/js/pages/contacts/hooks/useSaveContact';
import { ContactFormValues } from 'assets/js/pages/contacts/types';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useQueryParams } from 'customHooks/useQueryParams';

const CreateContactFormDrawer = () => {
    const { accountId } = useCurrentAccount();
    const queryParams = useQueryParams<ContactFormParams>();
    const [isAddingContactFile, setIsAddingContactFile] = useState(false);
    const autoFocusField = queryParams.autoFocus;

    const defaultValues = useDefaultContactFormValues();

    const methods = useGivebutterForm<ContactFormValues>({
        defaultValues,
    });

    const { reset, handleSubmit, setFocus, watch } = methods;
    const [shouldExitAfterCreate, setShouldExitAfterCreate] = useState(true);
    const ref = useRef<HTMLDivElement>(null);

    const history = useHistory();

    const contactType = watch('type');

    const { mutate: submit, isPending } = useSaveContact(methods, {
        onSuccess: () => {
            if (shouldExitAfterCreate) {
                history.replace(
                    `/accounts/${accountId}/${contactType === ContactType.Individual ? 'contacts' : 'companies'}`,
                );
            } else {
                reset({});
                ref.current?.scroll({ top: 0 });
                setFocus('firstName');
            }
        },
    });

    useEffect(() => {
        if (autoFocusField) {
            setTimeout(() => setFocus(autoFocusField), 0);
        }
    }, [autoFocusField, setFocus]);

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ContactFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Create New Contact</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body ref={ref}>
                    <ContactForm.Container>
                        <CompanyForm.ContactTypeToggle />

                        {contactType === ContactType.Company ? (
                            <>
                                <CompanyForm.Name />
                                <ContactForm.Addresses />
                                <ContactForm.Emails name="companyEmails" />
                                <ContactForm.Phones name="companyPhones" />
                                <ContactForm.Separator />
                                <CompanyForm.PointOfContact />
                                <ContactForm.Separator />
                                <ContactForm.Socials />
                            </>
                        ) : (
                            <>
                                <ContactForm.FirstName required={false} />
                                <ContactForm.MiddleName />
                                <ContactForm.LastName required={false} />
                                <ContactForm.Dob />
                                <ContactForm.Gender />
                                <ContactForm.Employer />
                                <CompanyForm.CompanySelectForm />
                                <ContactForm.Title />
                                <ContactForm.Separator />
                                <ContactForm.Emails />
                                <ContactForm.Separator />
                                <ContactForm.Phones />
                                <ContactForm.Separator />
                                <ContactForm.Addresses
                                    onPrefilled={() => setFocus('allowEngageEmail')}
                                />
                            </>
                        )}
                        <ContactForm.Separator />
                        <ContactForm.Notes />
                        <ContactForm.Tags />
                        <ContactForm.Separator />
                        <ContactForm.CustomFields
                            onAddContactFile={setIsAddingContactFile}
                            type={contactType}
                        />
                        <ContactForm.Separator />
                        <ContactForm.Engage />
                    </ContactForm.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant={'transparent'}>Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton
                                variant={'outline'}
                                color={'neutral'}
                                noAnimation={true}
                                onClick={() => setShouldExitAfterCreate(false)}
                                isSubmitting={isAddingContactFile || isPending}
                            >
                                Create and add another
                            </Form.SubmitButton>
                            <Form.SubmitButton
                                onClick={() => setShouldExitAfterCreate(true)}
                                isSubmitting={isAddingContactFile || isPending}
                            >
                                Create
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const CreateContactFormDrawerTrigger = Drawer.Trigger;

const Root = CreateContactFormDrawer;
const Trigger = CreateContactFormDrawerTrigger;

export {
    CreateContactFormDrawer,
    CreateContactFormDrawerTrigger,
    //
    Root,
    Trigger,
};
